import { useCallback, useEffect, useRef } from "react";

interface Props {
  handlePlaybackEnded: () => void;
  handlePlaybackPlaying: () => void;
  enabled: boolean;
}

const useFibiMediaSource = ({
  handlePlaybackEnded,
  handlePlaybackPlaying,
  enabled,
}: Props) => {
  const audioElement = useRef(enabled ? new Audio() : null);
  const mediaSourceRef = useRef(enabled ? new MediaSource() : null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const pendingChunks = useRef<Uint8Array[]>([]);
  const endOfStreamReceived = useRef(false);

  const processQueuedChunks = useCallback(() => {
    if (!enabled) return;
    if (!sourceBufferRef.current || sourceBufferRef.current.updating) return;

    // Append queued chunks first
    if (pendingChunks.current.length > 0) {
      const chunk = pendingChunks.current.shift();
      if (!chunk) return;
      sourceBufferRef.current.appendBuffer(chunk as any);
    }
    // Close stream only AFTER all chunks are appended
    else if (endOfStreamReceived.current) {
      mediaSourceRef?.current?.endOfStream();
      endOfStreamReceived.current = false;
    }
  }, [enabled]);

  const initialStream = useCallback(() => {
    if (!enabled) return;
    if (!audioElement.current) return;
    if (!mediaSourceRef.current) return;

    audioElement.current.src = URL.createObjectURL(mediaSourceRef.current);

    mediaSourceRef.current.addEventListener("sourceopen", () => {
      if (!mediaSourceRef.current) return;
      sourceBufferRef.current =
        mediaSourceRef.current.addSourceBuffer("audio/mpeg");

      // Process any queued chunks if needed
      processQueuedChunks();

      // Setup updateend handler to continue processing incoming chunks
      sourceBufferRef.current.addEventListener(
        "updateend",
        processQueuedChunks
      );
    });
  }, [enabled, processQueuedChunks]);

  const initNewStream = useCallback(() => {
    if (!enabled) return;
    // Stop current playback if necessary
    audioElement?.current?.pause();

    // Create a new MediaSource instance
    mediaSourceRef.current = new MediaSource();

    // Clear pending chunks
    pendingChunks.current = [];

    // Reset the source buffer reference
    sourceBufferRef.current = null;

    initialStream();
  }, [enabled, initialStream]);

  // First useEffect: Initialize audio element and MediaSource (only when enabled changes)
  useEffect(() => {
    if (!enabled) return;
    if (!audioElement.current) return;

    initialStream();

    return () => {
      // Cleanup MediaSource and SourceBuffer
      if (sourceBufferRef.current) {
        sourceBufferRef.current.removeEventListener(
          "updateend",
          processQueuedChunks
        );
      }

      // Pause and clean up the audio
      if (audioElement.current) {
        audioElement.current.pause();
        audioElement.current.src = "";
        audioElement.current.load(); // clears the buffer
      }

      audioElement.current = null;
      mediaSourceRef.current = null;
      sourceBufferRef.current = null;
    };
  }, [enabled, initialStream, processQueuedChunks]);

  // Second useEffect: Handle event listener updates (when handlers change)
  useEffect(() => {
    if (!enabled || !audioElement.current) return;

    const audioEl = audioElement.current;

    // Add event listeners
    audioEl.addEventListener("ended", handlePlaybackEnded);
    audioEl.addEventListener("playing", handlePlaybackPlaying);

    return () => {
      // Remove event listeners
      audioEl.removeEventListener("ended", handlePlaybackEnded);
      audioEl.removeEventListener("playing", handlePlaybackPlaying);
    };
  }, [handlePlaybackEnded, handlePlaybackPlaying, enabled]);

  return {
    processQueuedChunks,
    pendingChunks,
    audioElement,
    endOfStreamReceived,
    initNewStream,
    sourceBufferRef,
  };
};

export default useFibiMediaSource;
